// Signal-backed store for reminders.
// Follows the lazy-load / refresh pattern established in favorites.ts.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { Reminder, ReminderStatus } from "@juuplan/shared";
import { api } from "../lib/api";

// ---------- state ----------

export interface RemindersState {
  reminders: Reminder[];
  loading: boolean;
}

const remindersSignal: Signal<RemindersState> = signal({ reminders: [], loading: true });
let remindersStarted = false;

async function loadReminders(status?: ReminderStatus): Promise<void> {
  try {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    const data = await api<Reminder[]>(`/api/reminders${qs}`, { silentError: true });
    remindersSignal.value = { reminders: data ?? [], loading: false };
  } catch {
    remindersSignal.value = { ...remindersSignal.value, loading: false };
  }
}

export function useReminders(): RemindersState {
  useEffect(() => {
    if (!remindersStarted) {
      remindersStarted = true;
      void loadReminders();
    }
  }, []);
  return remindersSignal.value;
}

export function refreshReminders(): Promise<void> {
  return loadReminders();
}

export async function createReminder(body: {
  task_id?: number;
  personal_task_id?: number;
  remind_at: string;
}): Promise<Reminder> {
  const result = await api<Reminder>("/api/reminders", { method: "POST", body });
  await loadReminders();
  return result;
}

export async function cancelReminder(id: number): Promise<Reminder> {
  const result = await api<Reminder>(`/api/reminders/${id}`, {
    method: "PATCH",
    body: { status: "cancelled" },
  });
  await loadReminders();
  return result;
}

export async function deleteReminder(id: number): Promise<void> {
  await api<void>(`/api/reminders/${id}`, { method: "DELETE" });
  await loadReminders();
}
